import React, { useEffect, useRef } from "react";
import { ChevronLeft, House, Coins, HelpCircle, DoorOpen, LayoutGrid, Undo2, Sofa, Paintbrush, Check, RotateCw, Move, Trash2, X, Store, Moon } from "lucide-react";
import { pill, roundBtn, SAFE_TOP, SAFE_BOTTOM, GLASS, FONT, glassInner, glassBox } from "../kota/ui";
import { PanelBawah } from "./RumahUI";
import { tombolAksen } from "./KebunMode";
import { gambarIkonPerabot } from "../../lib/interiorRender";

// Bar atas ruang dalam: ← keluar · pil "Dalam Rumahku / @user" · pil koin (pemilik) · ?
export function DalamTopBar({ pemilik, saya, saldo, tata, onKeluar, onBantuan }) {
  return (
    <div data-testid="dalam-topbar" style={{ position: "absolute", left: 12, right: 12, top: `calc(${SAFE_TOP} + 12px)`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, pointerEvents: "none", fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <button data-testid="dalam-keluar" aria-label={tata ? "Batalkan penataan" : "Keluar rumah"} onClick={onKeluar} style={{ ...roundBtn(false), width: 38, height: 38, pointerEvents: "auto" }}>{tata ? <X size={17} strokeWidth={2} /> : <ChevronLeft size={18} strokeWidth={2} />}</button>
        <div data-testid="dalam-pill" style={{ ...pill, height: 46, padding: "0 14px 0 6px", gap: 8, minWidth: 0 }}>
          <span style={{ width: 34, height: 34, borderRadius: 99, background: GLASS.accentBg, color: GLASS.accent, display: "grid", placeItems: "center", flex: "none" }}><House size={17} strokeWidth={2} /></span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1, minWidth: 0 }}>
            <b style={{ fontSize: 13, fontWeight: 800, letterSpacing: -0.2 }}>{tata ? "Tata rumah" : saya ? "Dalam Rumahku" : "Dalam Rumah"}</b>
            <span style={{ fontSize: 10.5, color: GLASS.sub, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>@{pemilik?.ig_username}</span>
          </span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {saya && <span data-testid="dalam-saldo" style={{ ...pill, height: 38, padding: "0 12px", gap: 6, fontWeight: 800 }}><Coins size={14} color="#d9a21b" /> {saldo ?? "–"}</span>}
        <button data-testid="dalam-bantuan" aria-label="Petunjuk" onClick={onBantuan} style={{ ...roundBtn(false), width: 38, height: 38, pointerEvents: "auto" }}><HelpCircle size={17} strokeWidth={2} /></button>
      </div>
    </div>
  );
}

// Tab ruang (pil kaca bersegmen): Dapur · Ruang Tamu · Kamar · Semua → kamera fokus
export function RuangTabs({ ruang, aktif, onPilih }) {
  const items = [...Object.entries(ruang).map(([k, r]) => [k, r.nama]), ["semua", "Semua"]];
  return (
    <div data-testid="dalam-ruang-tabs" style={{ position: "absolute", left: 0, right: 0, top: `calc(${SAFE_TOP} + 68px)`, display: "flex", justifyContent: "center", pointerEvents: "none", fontFamily: FONT }}>
      <div style={{ pointerEvents: "auto", display: "flex", gap: 2, padding: 4, ...glassBox, borderRadius: 99 }}>
        {items.map(([k, nama]) => {
          const on = aktif === k;
          return <button key={k} data-testid={`ruang-tab-${k}`} aria-pressed={on} onClick={() => onPilih(k)} style={{ height: 32, padding: "0 12px", borderRadius: 99, ...glassInner(on), color: on ? GLASS.accent : GLASS.ink, fontWeight: on ? 800 : 600, fontSize: 12, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>{nama}</button>;
        })}
      </div>
    </div>
  );
}

// Dock bawah mode jalan-jalan: Keluar · Mall (pemilik) · Tata rumah (pemilik)
export function DalamDock({ saya, onKeluar, onTata, onMall }) {
  const btn = (tid, Icon, label, onClick, utama) => (
    <button data-testid={tid} onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 7, height: 44, padding: "0 16px", borderRadius: 99, ...glassInner(!!utama), color: utama ? GLASS.accent : GLASS.ink, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: FONT, boxShadow: utama ? "0 4px 12px rgba(20,20,20,0.06)" : "none" }}>
      <Icon size={18} strokeWidth={1.8} /> {label}
    </button>
  );
  return (
    <PanelBawah testid="dalam-dock" style={{ padding: `8px 10px calc(${SAFE_BOTTOM} + 10px)` }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {btn("dalam-dock-keluar", DoorOpen, "Keluar", onKeluar)}
        {saya && onMall && btn("dalam-dock-mall", Store, "Mall", onMall)}
        {saya && btn("dalam-dock-tata", LayoutGrid, "Tata rumah", onTata, true)}
      </div>
    </PanelBawah>
  );
}

// Bar mode Tata: Undo · Perabot · Gaya · n/48 slot · Simpan (atau "Beli & Simpan · harga" saat mencoba barang Mall yang belum dimiliki)
export function TataBar({ slot, slotMaks, bisaUndo, busy, berubah, beli, onUndo, onPerabot, onGaya, onSimpan }) {
  const ikon = (tid, Icon, label, onClick, disabled) => (
    <button data-testid={tid} aria-label={label} title={label} disabled={disabled} onClick={onClick} style={{ width: 42, height: 42, borderRadius: 99, ...glassInner(false), border: "1px solid var(--g-border)", color: GLASS.ink, display: "grid", placeItems: "center", cursor: disabled ? "default" : "pointer", opacity: disabled ? .4 : 1 }}>
      <Icon size={18} strokeWidth={1.8} />
    </button>
  );
  return (
    <PanelBawah testid="tata-bar" style={{ padding: `8px 10px calc(${SAFE_BOTTOM} + 10px)` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {ikon("tata-undo", Undo2, "Urungkan", onUndo, !bisaUndo)}
        {ikon("tata-perabot", Sofa, "Perabot", onPerabot)}
        {ikon("tata-gaya", Paintbrush, "Gaya ruangan", onGaya)}
        <span data-testid="tata-slot" style={{ flex: 1, textAlign: "center", fontSize: 12, fontWeight: 800, color: GLASS.ink }}>{slot}<span style={{ color: GLASS.sub, fontWeight: 600 }}>/{slotMaks} slot</span></span>
        <button data-testid="tata-simpan" disabled={busy || !berubah} onClick={onSimpan} style={{ ...tombolAksen, height: 42, opacity: busy || !berubah ? .5 : 1, ...(beli ? { background: GLASS.accent, color: "#fff", border: 0 } : {}) }}>{beli ? <><Coins size={15} /> Beli & Simpan · {beli.harga}</> : <><Check size={15} /> Simpan</>}</button>
      </div>
    </PanelBawah>
  );
}

// Popover perabot terpilih (mengikuti posisi di kanvas tiap frame): Putar · Pindah · Singkirkan
export function PopoverPerabot({ nama, posisi, onPutar, onPindah, onSingkirkan }) {
  const ref = useRef(null);
  // dijepit agar tidak terpotong tepi layar
  useEffect(() => { let raf; const tick = () => { const p = posisi(); if (p && ref.current) { const hw = ref.current.offsetWidth / 2 + 8; ref.current.style.left = `${Math.max(hw, Math.min(window.innerWidth - hw, p[0]))}px`; ref.current.style.top = `${p[1]}px`; } raf = requestAnimationFrame(tick); }; raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf); }, [posisi]);
  const aksi = (tid, Icon, label, onClick) => (
    <button data-testid={tid} onClick={e => { e.stopPropagation(); onClick(); }} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, background: "transparent", border: 0, cursor: "pointer", fontFamily: FONT, color: GLASS.ink, padding: 0 }}>
      <span style={{ width: 40, height: 40, borderRadius: 99, ...glassInner(true), display: "grid", placeItems: "center", color: GLASS.accent }}><Icon size={17} strokeWidth={1.9} /></span>
      <span style={{ fontSize: 10, fontWeight: 700 }}>{label}</span>
    </button>
  );
  return (
    <div ref={ref} data-testid="perabot-popover" onClick={e => e.stopPropagation()} style={{ position: "absolute", transform: "translate(-50%, calc(-100% - 18px))", ...glassBox, background: GLASS.bgStrong, borderRadius: 20, padding: "8px 12px 10px", fontFamily: FONT, zIndex: 6 }}>
      <div data-testid="perabot-popover-nama" style={{ textAlign: "center", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>{nama}</div>
      <div style={{ display: "flex", gap: 14 }}>
        {aksi("perabot-putar", RotateCw, "Putar", onPutar)}
        {aksi("perabot-pindah", Move, "Pindah", onPindah)}
        {aksi("perabot-singkirkan", Trash2, "Singkirkan", onSingkirkan)}
      </div>
    </div>
  );
}

// Pil petunjuk singkat di atas panel bawah (mis. "Ketuk lantai untuk menaruh Kursi")
export function HintPill({ teks, onBatal }) {
  return (
    <div data-testid="dalam-hint" style={{ position: "absolute", left: 0, right: 0, bottom: `calc(${SAFE_BOTTOM} + 84px)`, display: "flex", justifyContent: "center", pointerEvents: "none", fontFamily: FONT }}>
      <span style={{ ...pill, pointerEvents: "auto", height: 38, gap: 8, fontSize: 12 }}>{teks}{onBatal && <button data-testid="dalam-hint-batal" onClick={onBatal} aria-label="Batal" style={{ width: 24, height: 24, borderRadius: 99, border: 0, background: GLASS.inner, color: GLASS.ink, display: "grid", placeItems: "center", cursor: "pointer" }}><X size={13} /></button>}</span>
    </div>
  );
}

// Pil hitung mundur tidur (karakter berbaring di kasur, zzz) + tombol Bangun lebih awal
export function TidurPill({ sisa, onBangun }) {
  const m = Math.floor(sisa / 60), d = String(sisa % 60).padStart(2, "0");
  return (
    <div data-testid="tidur-pill" style={{ position: "absolute", left: 0, right: 0, bottom: `calc(${SAFE_BOTTOM} + 84px)`, display: "flex", justifyContent: "center", pointerEvents: "none", fontFamily: FONT, zIndex: 5 }}>
      <span style={{ ...pill, pointerEvents: "auto", height: 44, gap: 10, fontSize: 12.5, padding: "0 8px 0 14px" }}>
        <Moon size={15} color={GLASS.accent} />
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}><b style={{ fontSize: 12.5 }}>Sedang tidur… zzz</b><span data-testid="tidur-sisa" style={{ fontSize: 11, color: GLASS.sub, fontWeight: 700 }}>Bangun dalam {m}:{d}</span></span>
        <button data-testid="tidur-bangun" onClick={onBangun} style={{ height: 30, padding: "0 12px", borderRadius: 99, border: "none", background: GLASS.accentBg, color: GLASS.accent, fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: FONT }}>Bangun</button>
      </span>
    </div>
  );
}

// Ikon perabot: digambar dari renderer yang sama (tampilan konsisten dengan kanvas)
export function IkonPerabot({ kat, keyName, size = 64 }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) gambarIkonPerabot(ref.current, kat, keyName); }, [kat, keyName, size]);
  return <canvas ref={ref} width={size} height={size} style={{ width: size, height: size, display: "block" }} aria-hidden />;
}
